/* eslint-disable react-hooks/exhaustive-deps */
/*
 * Watchlist sub-tab — players being watched. Setting auto_ban=true means
 * the cheat-detection loop will auto-ban this player on the FIRST hit,
 * with no further threshold required.
 */
import { useEffect, useState, useCallback } from "react";
import { api, apiError } from "@/lib/api";
import { Eye, Plus, Trash2 } from "lucide-react";

export default function WatchlistTab({ toast }) {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ player_id: "", player_name: "", reason: "", auto_ban: false });
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/moderation/watchlist");
      setRows(data.watchlist || []);
    } catch (e) { toast.error(apiError(e)); }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    if (!form.player_id || !form.reason) { toast.error("Player ID and reason are required"); return; }
    setSubmitting(true);
    try {
      await api.post("/admin/moderation/watchlist", form);
      toast.success(`Added ${form.player_id} to watchlist`);
      setForm({ player_id: "", player_name: "", reason: "", auto_ban: false });
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setSubmitting(false); }
  };

  const remove = async (pid) => {
    if (!window.confirm(`Remove ${pid} from watchlist?`)) return;
    try {
      await api.delete(`/admin/moderation/watchlist/${encodeURIComponent(pid)}`);
      toast.success(`Removed ${pid}`);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-4">
      <form onSubmit={add} className="glass-card p-4 space-y-3" data-testid="watch-add-form">
        <h3 className="label flex items-center gap-2"><Plus size={14} /> Add to Watchlist</h3>
        <div className="grid sm:grid-cols-2 gap-3">
          <input value={form.player_id} onChange={(e) => setForm({ ...form, player_id: e.target.value })} placeholder="Player ID" className="input" data-testid="watch-player-id" />
          <input value={form.player_name} onChange={(e) => setForm({ ...form, player_name: e.target.value })} placeholder="Player name (optional)" className="input" data-testid="watch-player-name" />
        </div>
        <input value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="Reason" className="input" data-testid="watch-reason" />
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={form.auto_ban} onChange={(e) => setForm({ ...form, auto_ban: e.target.checked })} data-testid="watch-autoban" />
          Auto-ban on next cheat detection (no threshold)
        </label>
        <button disabled={submitting} className="btn btn-teal" data-testid="watch-submit"><Eye size={14} /> Add</button>
      </form>

      <div className="glass-card p-4" data-testid="watch-list">
        {rows.length === 0 ? (
          <p className="text-[var(--muted)] text-sm">No players on the watchlist.</p>
        ) : (
          <ul className="divide-y divide-white/5">
            {rows.map((r) => (
              <li key={r.player_id} className="py-3 flex items-start gap-3" data-testid={`watch-row-${r.player_id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-sm text-[var(--bone)]">{r.player_name || r.player_id}</span>
                    {r.auto_ban ? (
                      <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-rose-500/20 text-rose-300">Auto-Ban</span>
                    ) : (
                      <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-teal-500/20 text-teal-300">Watch</span>
                    )}
                  </div>
                  <div className="text-xs text-[var(--muted)] mt-0.5 font-mono">{r.player_id}</div>
                  <p className="text-sm text-white/85 mt-1">{r.reason}</p>
                </div>
                <button onClick={() => remove(r.player_id)} className="btn btn-ghost text-rose-300" data-testid={`watch-remove-${r.player_id}`}><Trash2 size={14} /></button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
